import { useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiClientError } from 'api/client';
import {
  refreshUserPermissions,
  useRefreshBillingEntitlements,
} from 'app/modules/settings/modules/billing/hooks/useRefreshBillingEntitlements';
import { queryKey } from 'constants/queryKey';
import {
  BillingAddress,
  BillingApi,
  GetInvoicesParams,
  VatNumberSuggestionParams,
  VatNumberSuggestionResponse,
  VatValidationStatus,
} from 'app/modules/settings/modules/billing/api/billing';
import {
  Addon,
  AddonItem,
  AttachBankTransferRequest,
  AttachPaymentMethodRequest,
  BillingCycle,
  DeletePaymentMethodResponse,
  Invoice,
  InvoiceItem,
  ModuleItem,
  ModuleKey,
  NextInvoice,
  PaymentMethod,
  PlanItem,
  ProductModule,
  SetDefaultPaymentMethodResponse,
  SocialProofResponse,
  SubscribeRequest,
  SubscribeResponse,
  Subscription,
  SubscriptionResponse,
  Testimonial,
  TopUpAiCreditsResponse,
  TrialInfo,
  TrialResponse,
  UnlockedFeaturesResponse,
  UpdateAddonsRequest,
  UsageMeter,
  UsageResponse,
} from 'app/modules/settings/modules/billing/types';
import { shouldRetryTransientOnly } from 'app/modules/settings/modules/billing/utils/apiError';
import { getAddonPresentation } from 'app/modules/settings/modules/billing/utils/addonsCatalog';
import { invalidateBillingCaches } from 'app/modules/settings/modules/billing/utils/billingCaches';

// ---------------------------------------------------------------------------
// Billing queries: refetch model
//
// Every billing query shares a 5-minute `staleTime`. Billing state only
// changes via (a) explicit user actions — handled by surgical invalidation
// inside the mutation hooks below — and (b) Stripe/Braintree webhooks the FE
// cannot subscribe to directly. We never poll.
//
// Webhook-driven refetch:
//
//   - When the cached subscription is in a "waiting for webhook" state
//     (`incomplete` or `past_due`), `useSubscription`, `useInvoices`, and
//     `useInvoicesInfinite` opt into `refetchOnWindowFocus`. This catches
//     the canonical case: user clicks the 3DS confirmation link in an
//     email, completes the challenge in another tab, returns here.
//
//   - The check reads from the QueryClient cache on every focus event,
//     so as soon as the BE flips the subscription to `active`, the next
//     focus is a no-op — no manual subscribe/unsubscribe.
//
//   - We pass 'always' (not true) to `refetchOnWindowFocus` because the
//     5-minute `staleTime` would otherwise suppress the focus refetch
//     inside the staleness window — exactly the case we care about.
//
//   - There is a race: the user can return to the tab before Stripe's
//     webhook has reached the BE. To cover it, `useSubscription` schedules
//     one followup `invalidateQueries` ~2s after any refetch that
//     resolved with the subscription still in a pending state. This is
//     per-resolution — once `active` lands, the chain terminates.
//
//   - `payment_method.automatically_updated` (handoff §5) is not handled
//     here. The 5-min staleTime means a card auto-refresh can render
//     stale last4/brand/exp for up to that window. Accepted as a
//     low-impact edge case.
// ---------------------------------------------------------------------------

const BILLING_STALE_TIME = 5 * 60 * 1000;

export const isAwaitingWebhook = (
  status: Subscription['status'] | undefined,
): boolean => status === 'incomplete' || status === 'past_due';

// The followup chain covers the 3DS webhook race, but it must be bounded: a
// subscription stuck in a pending status (e.g. an INCOMPLETE sub whose invoice
// / payment intent was never created, so no webhook will ever arrive) would
// otherwise self-rearm every 2s forever and leave the page perpetually
// refreshing. After this many consecutive followups (~MAX × 2s) we stop
// auto-polling; a window focus or the PendingPaymentBanner's manual refresh
// still picks up a genuinely late webhook.
export const MAX_WEBHOOK_FOLLOWUPS = 30;

const refetchInvoicesOnWebhookPending =
  (queryClient: QueryClient) => (): false | 'always' => {
    const sub = queryClient.getQueryData<SubscriptionResponse>(
      queryKey.billingSubscription(),
    );
    return isAwaitingWebhook(sub?.subscription?.status) ? 'always' : false;
  };

export const useSubscription = () => {
  const queryClient = useQueryClient();
  const followupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followupCountRef = useRef(0);

  useEffect(
    () => () => {
      if (followupTimerRef.current !== null) {
        clearTimeout(followupTimerRef.current);
        followupTimerRef.current = null;
      }
    },
    [],
  );

  return useQuery(
    queryKey.billingSubscription(),
    () => BillingApi.getSubscription(),
    {
      staleTime: BILLING_STALE_TIME,
      // A 404 here is the api's "this account is not on NewBilling" answer, and
      // it is a terminal one. Without these two options it becomes a request
      // storm instead:
      //
      //   1. `useSubscriptionInfo` -> `useIsNewBillingEnabled` ->
      //      `useNewBillingRoutesAvailable` -> this query. ~40 components across
      //      the app mount it, so observers come and go constantly.
      //   2. The query never holds data on the error path, so `retryOnMount`
      //      (default true) makes EVERY newly mounted observer re-fire it
      //      (react-query `shouldLoadOnMount`).
      //   3. Each refetch resets `error: null, status: 'loading'` (react-query
      //      `query.js` 'fetch' reducer, because `dataUpdatedAt` is 0), so
      //      `useNewBillingRoutesAvailable` flips back to `true` mid-flight and
      //      re-enables the entitlements query, which 404s too.
      //   4. Both errors re-render all ~40 consumers, remounting subtrees ->
      //      back to (2). Self-sustaining, ~40x the normal request rate.
      //
      // `retry` keeps a genuine transient 5xx recoverable, so making the error
      // sticky does not strand the billing page on a one-off blip.
      retry: shouldRetryTransientOnly,
      retryOnMount: false,
      refetchOnWindowFocus: (query) =>
        isAwaitingWebhook(query.state.data?.subscription?.status)
          ? 'always'
          : false,
      onSuccess: (data) => {
        if (followupTimerRef.current !== null) {
          clearTimeout(followupTimerRef.current);
          followupTimerRef.current = null;
        }
        if (!isAwaitingWebhook(data.subscription?.status)) {
          // Reached a terminal status — reset so a future pending state (e.g. a
          // fresh checkout) re-arms the chain from scratch.
          followupCountRef.current = 0;
          return;
        }
        if (followupCountRef.current >= MAX_WEBHOOK_FOLLOWUPS) {
          // No webhook is arriving — stop auto-polling. Window focus and the
          // manual refresh still cover a genuinely late one.
          return;
        }
        followupCountRef.current += 1;
        followupTimerRef.current = setTimeout(() => {
          followupTimerRef.current = null;
          queryClient.invalidateQueries(queryKey.billingSubscription());
        }, 2000);
      },
    },
  );
};

export const useUsage = () =>
  useQuery(queryKey.billingUsage(), () => BillingApi.getUsage(), {
    staleTime: BILLING_STALE_TIME,
  });

export const useNextInvoice = () =>
  useQuery(queryKey.billingNextInvoice(), () => BillingApi.getNextInvoice(), {
    staleTime: BILLING_STALE_TIME,
  });

export const usePaymentMethod = () =>
  useQuery(
    queryKey.billingPaymentMethod(),
    () => BillingApi.getPaymentMethod(),
    { staleTime: BILLING_STALE_TIME },
  );

/**
 * Full manageable-methods list (`GET /billing/payment-methods`). Drives the
 * manage modal and the dashboard card's "N more payment methods saved"
 * footer. `enabled` lets the page skip the fetch outside the paid layout.
 */
export const usePaymentMethods = (enabled = true) =>
  useQuery(
    queryKey.billingPaymentMethods(),
    () => BillingApi.getPaymentMethods(),
    { enabled, staleTime: BILLING_STALE_TIME },
  );

/**
 * Fetch a Braintree client token for the in-browser Drop-in. Disabled by
 * default — the modal flips `enabled` only while it is open so we never
 * burn a token until the user actually starts adding a method. We also
 * skip the cache (`cacheTime: 0`, `staleTime: 0`) because tokens are
 * short-lived and re-using a stale one yields a confusing tokenizer error.
 */
export const usePaymentMethodClientToken = (enabled: boolean) =>
  useQuery(
    queryKey.billingPaymentMethodClientToken(),
    () => BillingApi.getPaymentMethodClientToken(),
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      retry: false,
    },
  );

export const useInvoices = (params?: GetInvoicesParams) => {
  const queryClient = useQueryClient();
  return useQuery(
    queryKey.billingInvoices(params),
    () => BillingApi.getInvoices(params),
    {
      staleTime: BILLING_STALE_TIME,
      refetchOnWindowFocus: refetchInvoicesOnWebhookPending(queryClient),
    },
  );
};

export const useInvoicesInfinite = (params: { limit?: number } = {}) => {
  const limit = params.limit ?? 5;
  const queryClient = useQueryClient();
  return useInfiniteQuery(
    queryKey.billingInvoicesInfinite({ limit }),
    ({ pageParam }: { pageParam?: string }) =>
      BillingApi.getInvoices({ limit, cursor: pageParam }),
    {
      getNextPageParam: (lastPage) => lastPage.paging?.nextCursor ?? undefined,
      staleTime: BILLING_STALE_TIME,
      refetchOnWindowFocus: refetchInvoicesOnWebhookPending(queryClient),
    },
  );
};

/**
 * Resolve bankwire funding instructions for a single PENDING offline invoice.
 * Disabled until an `invoiceId` is supplied (the panel passes the id of the
 * latest pending offline invoice). We skip caching across the session
 * (`staleTime: 0`) because the BE makes a live Stripe call and the wire
 * details can change while the funding intent is open; refetch on focus so a
 * user returning after wiring sees the settled state.
 */
export const useInvoicePaymentInstructions = (
  invoiceId: string | null | undefined,
) =>
  useQuery(
    queryKey.billingInvoicePaymentInstructions(invoiceId ?? ''),
    () => BillingApi.getInvoicePaymentInstructions(invoiceId as string),
    {
      enabled: Boolean(invoiceId),
      staleTime: 0,
      refetchOnWindowFocus: 'always',
    },
  );

export const useAddons = () =>
  useQuery(queryKey.billingAddons(), () => BillingApi.getAddons(), {
    staleTime: BILLING_STALE_TIME,
  });

export const useModules = () =>
  useQuery(queryKey.billingModules(), () => BillingApi.getModules(), {
    staleTime: BILLING_STALE_TIME,
  });

export const usePlans = () =>
  useQuery(queryKey.billingPlans(), () => BillingApi.getPlans(), {
    staleTime: BILLING_STALE_TIME,
  });

export const useTrial = (enabled = true) =>
  useQuery(queryKey.billingTrial(), () => BillingApi.getTrial(), {
    enabled,
    staleTime: BILLING_STALE_TIME,
  });

export const useTrialUnlockedFeatures = (enabled = true) =>
  useQuery(
    queryKey.billingTrialUnlockedFeatures(),
    () => BillingApi.getTrialUnlockedFeatures(),
    { enabled, staleTime: BILLING_STALE_TIME },
  );

export const useTrialSocialProof = (enabled = true) =>
  useQuery(
    queryKey.billingTrialSocialProof(),
    () => BillingApi.getTrialSocialProof(),
    { enabled, staleTime: BILLING_STALE_TIME },
  );

// ---------------------------------------------------------------------------
// Adapters — turn spec-shaped API responses into the UI-facing shapes the
// existing components expect. Keep components unaware of the envelope split.
// ---------------------------------------------------------------------------

const toSubscription = (
  sub: SubscriptionResponse['subscription'],
  usageRes: UsageResponse | undefined,
): Subscription | null => {
  if (!sub) return null;
  return {
    planId: sub.planId ?? '',
    planName: sub.planName ?? '',
    // The API serializes priceMonthly as a decimal STRING ("149.00"); coerce to
    // a number so arithmetic (e.g. the recurring total) adds instead of
    // string-concatenating.
    priceMonthly: Number(sub.priceMonthly ?? 0) || 0,
    // The amount actually charged per billing period. `priceMonthly` is a
    // derived monthly EQUIVALENT (period amount / months in cycle, rounded to
    // 2dp), so it cannot be multiplied back up: a €200.00/yr plan serializes as
    // "16.67", and 16.67 × 12 renders €200.04. Use this for any figure the
    // customer reads as "what I pay". Falls back to priceMonthly for safety if
    // the API predates the field.
    pricePeriod: Number(sub.pricePeriod ?? sub.priceMonthly ?? 0) || 0,
    cycle: (sub.cycle ?? 'monthly') as BillingCycle,
    renewsOn: sub.renewsOn ?? '',
    renewsInDays: sub.renewsInDays ?? 0,
    // Absent on an api that predates the field — treat as "not ended" rather
    // than guessing from renewsInDays, which the api clamps to 0 for a past
    // date and so cannot distinguish the two.
    periodEnded: sub.periodEnded === true,
    status: sub.status,
    currency: sub.currency ?? 'EUR',
    bankwireEligible: sub.bankwireEligible === true,
    scheduledCancellation: sub.scheduledCancellation ?? null,
    scheduledCycleChange: sub.scheduledCycleChange ?? null,
    scheduledPlanChange: sub.scheduledPlanChange ?? null,
    scheduledPause: sub.scheduledPause ?? null,
    usage: (usageRes?.usage ?? []) as UsageMeter[],
  };
};

const toNextInvoice = (
  n: import('app/modules/settings/modules/billing/types').NextInvoiceResponse['nextInvoice'],
): NextInvoice | null => {
  if (!n) return null;
  return {
    lines: n.lines,
    discountLines: n.discountLines ?? [],
    subtotal: n.subtotal ?? n.total,
    taxPercent: n.taxPercent ?? 0,
    taxValue: n.taxValue ?? 0,
    total: n.total,
    currency: n.currency ?? 'EUR',
    dueOn: n.dueOn ?? '',
    dueInDays: n.dueInDays ?? 0,
    cardLast4: n.cardLast4 ?? '',
    cardBrand: n.cardBrand ?? '',
  };
};

const toPaymentMethod = (
  pm: import('app/modules/settings/modules/billing/types').PaymentMethodResponse['paymentMethod'],
): PaymentMethod | null => {
  if (!pm) return null;
  return {
    // Older accounts persisted before the Drop-in landing have no `kind`
    // field; default to 'card' so `PaymentMethodCard` keeps rendering the
    // legacy single-card UI for those rows.
    kind: pm.kind ?? 'card',
    brand: pm.brand ?? '',
    last4: pm.last4 ?? '',
    holderName: pm.holderName ?? '',
    // Keep expiry nullable through to the card — `PaymentMethodCard`
    // renders an em dash when either side is null instead of showing a
    // fabricated "exp 00/00".
    expMonth: pm.expMonth,
    expYear: pm.expYear,
    billingEmail: pm.billingEmail ?? '',
    paypalEmail: pm.paypalEmail ?? null,
    walletType: pm.walletType ?? null,
  };
};

const toInvoice = (i: InvoiceItem): Invoice => ({
  id: i.id,
  number: i.number,
  date: i.date,
  // Credit-note rows carry no description (see InvoiceItem). Empty string
  // rather than `undefined` so the row renders blank instead of the word
  // "undefined"; whether a credit note should say something there — e.g.
  // "Credit note for KN-2026-0001", which the payload has the fields for — is
  // a copy decision, not a defect fix.
  description: i.description ?? '',
  amount: i.amount,
  currency: i.currency,
  status: i.status,
  downloadUrl: i.downloadUrl,
  dunningAttempts: i.dunningAttempts,
  nextAttemptAt: i.nextAttemptAt,
  documentType: i.documentType,
  source: i.source,
  type: i.type,
});

const toAddon = (a: AddonItem, t: TFunction): Addon => {
  const presentation = getAddonPresentation(a.key, t);
  return {
    key: a.key,
    title: a.label,
    description: presentation.description,
    unitPrice: a.unitAmount ?? 0,
    unitSuffix: presentation.unitSuffix,
    accent: presentation.accent,
    iconLabel: presentation.iconLabel,
    quantity: a.quantity,
    scheduledQuantity: a.scheduledQuantity ?? null,
    scheduledEffectiveAt: a.scheduledEffectiveAt ?? null,
    currency: a.currency,
  };
};

const toProductModule = (m: ModuleItem): ProductModule => {
  const allowedAccents: ProductModule['accent'][] = [
    'blue',
    'purple',
    'orange',
    'gray',
  ];
  const accent = (
    allowedAccents.includes(m.accent as ProductModule['accent'])
      ? m.accent
      : 'gray'
  ) as ProductModule['accent'];
  return {
    key: m.key,
    accent,
    iconLabel: m.iconLabel ?? '',
    tag: m.tag ?? '',
    title: m.title ?? '',
    description: m.description ?? '',
    state: m.state,
    source: m.source,
    priceLabel: m.priceLabel ?? undefined,
    priceSuffix: m.priceSuffix ?? undefined,
    priceMeta: m.priceMeta ?? undefined,
    waitlistCount: m.waitlistCount ?? undefined,
    waitlisted: m.waitlisted === true,
    scheduledMarkets: m.scheduledMarkets ?? undefined,
    effectiveUntil: m.effectiveUntil ?? undefined,
    cancelScheduled: m.cancelScheduled ?? false,
  };
};

const toTestimonial = (
  t: SocialProofResponse['testimonials'][number],
): Testimonial => {
  const allowed: Testimonial['avatarAccent'][] = ['blue', 'purple', 'orange'];
  const avatarAccent = (
    allowed.includes(t.avatarAccent as Testimonial['avatarAccent'])
      ? t.avatarAccent
      : 'blue'
  ) as Testimonial['avatarAccent'];
  return {
    id: t.id,
    author: t.author,
    role: t.role,
    quote: t.quote,
    company: t.company,
    tenure: t.tenure,
    avatarAccent,
    initials: t.initials,
  };
};

const toTrialInfo = (
  trialRes: TrialResponse['trial'],
  unlocked: UnlockedFeaturesResponse | undefined,
  social: SocialProofResponse | undefined,
): TrialInfo | undefined => {
  if (!trialRes) return undefined;
  return {
    dayOfTrial: trialRes.dayOfTrial,
    totalDays: trialRes.totalDays,
    daysLeft: trialRes.daysLeft,
    endsOn: trialRes.endsOn,
    hasCard: trialRes.hasCard,
    grade: trialRes.grade,
    usage: trialRes.usage ?? [],
    unlockedFeatures: unlocked?.unlockedFeatures ?? [],
    afterTrial: {
      keep: unlocked?.afterTrial?.keep ?? [],
      lose: unlocked?.afterTrial?.lose ?? [],
      proRatedNote: unlocked?.afterTrial?.proRatedNote ?? '',
      dataRetentionNote: unlocked?.afterTrial?.dataRetentionNote ?? '',
    },
    testimonials: (social?.testimonials ?? []).map(toTestimonial),
    trust: {
      stats: social?.trust?.stats ?? [],
      partners: social?.trust?.partners ?? [],
    },
  };
};

// ---------------------------------------------------------------------------
// Composite hooks — fire the parallel sets the spec recommends.
// ---------------------------------------------------------------------------

export type UseBillingDashboardOptions = {
  invoicesLimit?: number;
};

/**
 * Initial-load bundle for the paid/subscribed billing screen. Fires the
 * seven independent core queries in parallel (per spec §14 "Initial load").
 *
 * Returns individual queries (so callers can surface per-section loading
 * states) plus a convenience aggregate of whether every core query is still
 * loading / any core query failed, and a pre-adapted UI-facing snapshot.
 */
export const useBillingDashboard = ({
  invoicesLimit = 5,
}: UseBillingDashboardOptions = {}) => {
  const { t } = useTranslation();
  const subscription = useSubscription();
  const usage = useUsage();
  const nextInvoice = useNextInvoice();
  const paymentMethod = usePaymentMethod();
  const invoices = useInvoices({ limit: invoicesLimit });
  const addons = useAddons();
  const modules = useModules();
  const plans = usePlans();

  const adapted = useMemo(() => {
    const subData = subscription.data;
    if (!subData) return null;

    const sub = toSubscription(subData.subscription, usage.data);

    return {
      accountStatus: subData.accountStatus,
      subscription: sub,
      nextInvoice: toNextInvoice(nextInvoice.data?.nextInvoice ?? null),
      paymentMethod: toPaymentMethod(paymentMethod.data?.paymentMethod ?? null),
      invoices: (invoices.data?.data ?? []).map(toInvoice),
      invoicesNextCursor: invoices.data?.paging?.nextCursor ?? null,
      addons: (addons.data?.addons ?? []).map((a) => toAddon(a, t)),
      modules: (modules.data?.modules ?? []).map(toProductModule),
      plans: (plans.data?.plans ?? []) as PlanItem[],
    };
  }, [
    subscription.data,
    usage.data,
    nextInvoice.data,
    paymentMethod.data,
    invoices.data,
    addons.data,
    modules.data,
    plans.data,
    t,
  ]);

  // Subscription is the critical path — once it resolves the layout can
  // pick the trial/paid variant. The other sections have their own
  // `isLoading` so the page can show granular skeletons.
  const coreQueries = [
    subscription,
    usage,
    nextInvoice,
    paymentMethod,
    invoices,
    addons,
    modules,
  ];

  // Aggregate `isLoading` semantics:
  //   - Before subscription resolves we don't know trial vs paid — keep it
  //     true so the page shows its top-level skeleton.
  //   - Once we know the user is on a paid plan, wait for every core
  //     section too. Otherwise the dashboard would flash "No upcoming
  //     invoice" / empty addons while those queries are still in flight.
  //   - On trial the trial-specific bundle lives in `useTrialDashboard`;
  //     this hook's sections aren't shown, so subscription alone decides.
  // Per-section flags remain exposed separately so the layout can keep
  // showing granular skeletons after the overall page has started to paint.
  const isPaidLoading =
    subscription.data?.accountStatus === 'paid' &&
    coreQueries.some((q) => q.isLoading);
  const isLoading = subscription.isLoading || isPaidLoading;

  return {
    subscription,
    usage,
    nextInvoice,
    paymentMethod,
    invoices,
    addons,
    modules,
    plans,
    data: adapted,
    isLoading,
    isError: coreQueries.some((q) => q.isError),
  };
};

/**
 * Trial-screen bundle — `/trial`, `/trial/unlocked-features`,
 * `/trial/social-proof` are all independent (per spec). `usePlans` is
 * re-fired on the trial path too because the plan picker is visible there.
 */
export const useTrialDashboard = (enabled = true) => {
  const trial = useTrial();
  const unlocked = useTrialUnlockedFeatures(enabled);
  const social = useTrialSocialProof(enabled);
  const plans = usePlans();

  const trialInfo = useMemo(
    () => toTrialInfo(trial.data?.trial ?? null, unlocked.data, social.data),
    [trial.data, unlocked.data, social.data],
  );

  return {
    trial,
    unlocked,
    social,
    plans,
    trialInfo,
    isLoading: trial.isLoading,
    isError: trial.isError || unlocked.isError || social.isError,
  };
};

// ---------------------------------------------------------------------------
// Write-side mutations. Every hook invalidates the minimum set of query keys
// that could have changed — the caller surfaces success/error toasts.
// ---------------------------------------------------------------------------

export { refreshUserPermissions };

/**
 * Hook form of {@link invalidateBillingCaches} for components that need to
 * refresh billing state outside a mutation (e.g. after a modal closes).
 */
export const useInvalidateBillingCaches = (): (() => void) => {
  const queryClient = useQueryClient();

  return () => invalidateBillingCaches(queryClient);
};

/**
 * `POST /billing/subscribe` — changes plan + cycle. Per spec, invalidates:
 *   - `/subscription` (new plan + cycle)
 *   - `/next-invoice` (pro-rated recompute)
 *   - `/addons` (catalog prices can shift with plan tier)
 *   - `/plans` (the "current" badge moves to the new slug)
 */
export const useSubscribeMutation = () => {
  const refreshEntitlements = useRefreshBillingEntitlements();

  return useMutation<SubscribeResponse, unknown, SubscribeRequest>(
    (payload) => BillingApi.subscribe(payload, uuidv4()),
    {
      onSuccess: () => {
        refreshEntitlements();
      },
    },
  );
};

/**
 * `PUT /billing/addons` — persists new add-on quantities. Invalidates the
 * `/addons` key (fresh quantities), `/next-invoice` (pro-rated totals), and
 * the `/subscription` + `/usage` keys so the current-plan card's seat total
 * (used/total) reflects the new extra-seat count instead of going stale
 * (BUG-42).
 */
export const useUpdateAddonsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: UpdateAddonsRequest) =>
      BillingApi.updateAddons(payload, uuidv4()),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `POST /billing/modules/:key/activate` — branches by module state on the
 * backend: active modules become ACTIVE immediately, `coming` ones add a
 * waitlist row. Same endpoint for both paths.
 */
export const useActivateModuleMutation = () => {
  const refreshEntitlements = useRefreshBillingEntitlements();

  return useMutation((key: ModuleKey) => BillingApi.activateModule(key), {
    onSuccess: () => {
      refreshEntitlements();
    },
  });
};

/**
 * `POST /billing/modules/:key/cancel` — schedules the module to stop at the
 * end of the current period (universal). The module stays active + billed for
 * the current period, then is not renewed. No proration. Invalidate the
 * modules read (pending-change fields) and the next-invoice preview (renewal
 * total drops). Idempotent on the server.
 */
export const useCancelModuleMutation = () => {
  const refreshEntitlements = useRefreshBillingEntitlements();

  return useMutation((key: ModuleKey) => BillingApi.cancelModule(key), {
    onSuccess: () => {
      refreshEntitlements();
    },
  });
};

/**
 * `POST /subscription/reactivate` — reverses a pending end-of-period
 * cancellation (re-enables auto-renew). Refreshes the subscription (the
 * `scheduledCancellation` field clears) and the next-invoice preview (a
 * renewal charge is expected again).
 */
export const useReactivateMutation = () => {
  const refreshEntitlements = useRefreshBillingEntitlements();

  return useMutation(() => BillingApi.reactivateSubscription(), {
    onSuccess: () => {
      // Covers CN1 as well as the subscription/next-invoice keys #4566 listed
      // individually: invalidateBillingCaches invalidates the billing prefix AND
      // CANCELLATION_STATUS_KEY ('cancellation-status'), so it is a strict
      // superset of that list. Without the cancellation-status half, reopening
      // the cancellation flow in the same session still renders "Your request is
      // being processed" plus a one-click "Cancel permanently".
      //
      // Note the backend mechanism changed under this: api #949 no longer deletes
      // the account's cancellation rows on reactivate (that erased the churn
      // history every CS default-scope read depends on). The open/closed state is
      // now DERIVED from the subscription, so the cached status is stale for a
      // different reason than #4566's comment described — but it is equally stale,
      // and this invalidation is still required.
      refreshEntitlements();
    },
  });
};

/**
 * `PATCH /billing/modules/gcm/markets` — schedules a new GCM market count for
 * the next renewal (min 1). No proration. Same invalidation as cancel: the
 * modules read carries `scheduledMarkets` and the renewal total changes.
 */
export const useChangeGcmMarketsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (markets: number) => BillingApi.changeGcmMarkets(markets),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `POST /billing/payment-method` — stores a tokenized Braintree nonce.
 * Invalidates the current method (so the new last4/brand/expiry render) and
 * the next invoice card (card-on-file badge may change).
 */
export const useAttachPaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: AttachPaymentMethodRequest) =>
      BillingApi.attachPaymentMethod(payload),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

export const useBillingAddress = () =>
  useQuery(queryKey.billingAddress(), () => BillingApi.getBillingAddress(), {
    staleTime: BILLING_STALE_TIME,
  });

/**
 * `POST /billing/vat/validate` — live VIES check for a VAT number (api #869).
 * Advisory only: the caller debounces the input, fires this once the format
 * guard passes, and surfaces the `status` as a caption/error without blocking
 * submit (a `ServiceUnavailable` result must never gate the form).
 */
export const useValidateVatNumber = () =>
  useMutation<{ status: VatValidationStatus }, ApiClientError, string>(
    (vatNumber) => BillingApi.validateVatNumber(vatNumber),
  );

/**
 * The VAT number a Business ID stands for. A query, not a mutation: the
 * answer for a country + Business ID never changes within a session, so it
 * is cached, de-duplicated and never outlived by a slower earlier response.
 */
export const useVatNumberSuggestion = (
  params: VatNumberSuggestionParams | null,
) =>
  useQuery<VatNumberSuggestionResponse, ApiClientError>(
    queryKey.vatNumberSuggestion(
      params?.country ?? '',
      params?.businessId ?? '',
    ),
    () =>
      params
        ? BillingApi.suggestVatNumber(params)
        : Promise.resolve({ suggestion: null }),
    { enabled: params !== null, staleTime: Infinity, retry: false },
  );

export const useUpdateBillingAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: BillingAddress) => BillingApi.updateBillingAddress(body),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `PUT /billing/billing-email` (api #671) — changes only the billing email.
 * Invalidates the payment method (the card shows the billing email) and the
 * next invoice card (it surfaces the billing email too). No idempotency key —
 * the BE endpoint is a plain idempotent PUT.
 */
export const useUpdateBillingEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation((email: string) => BillingApi.updateBillingEmail(email), {
    onSuccess: () => {
      invalidateBillingCaches(queryClient);
    },
  });
};

/**
 * `POST /billing/payment-method/stripe-bank-transfer` — attaches an offline
 * bank-transfer method and makes it the account default. Idempotency-Key is
 * supplied by the caller so a transient 502 can be retried under the SAME key
 * (a fresh one would re-run the Stripe customer setup) — mirrors the
 * `useAttachStripePaymentMethod` contract. Invalidates the payment method
 * (the new default is the offline row), the next invoice (settlement basis
 * changed), and the subscription (offline path now drives renewals).
 */
export type AttachBankTransferVars = {
  body: AttachBankTransferRequest;
  idempotencyKey: string;
};

export const useAttachBankTransferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ body, idempotencyKey }: AttachBankTransferVars) =>
      BillingApi.attachBankTransferPaymentMethod(body, idempotencyKey),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `DELETE /billing/payment-method` — detaches the default method. Empty 204
 * response; caller reloads `/payment-method` to show the empty state card.
 */
export const useDetachPaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(() => BillingApi.detachPaymentMethod(), {
    onSuccess: () => {
      invalidateBillingCaches(queryClient);
    },
  });
};

/**
 * `POST /billing/payment-methods/{id}/default` — promote a saved method.
 * Invalidate-refetch per the module pattern: the list (ordering changes —
 * the promoted row moves to the top), the single default method (dashboard
 * card), and the next invoice (card-on-file line). Errors surface at the
 * call site (422 userMessage / 404·409 list-refresh, spec §Error handling).
 */
export const useSetDefaultPaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<SetDefaultPaymentMethodResponse, ApiClientError, number>(
    (methodId) => BillingApi.setDefaultPaymentMethod(methodId),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `DELETE /billing/payment-methods/{id}` — remove a saved method. The
 * response's `promoted` names the auto-promoted successor (never silent —
 * the caller toasts it). Same invalidation set as set-default.
 */
export const useDeletePaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<DeletePaymentMethodResponse, ApiClientError, number>(
    (methodId) => BillingApi.deletePaymentMethod(methodId),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `POST /billing/invoices/{id}/retry-charge` — manual retry of a failed
 * invoice during dunning. Always invalidates the dashboard slice on settle
 * (success and 4xx/5xx alike) because every non-200 response means the
 * invoice or subscription state has shifted on the BE: 200 flips invoice→PAID
 * + subscription→ACTIVE; 422/503 increments `dunningAttempts` and reschedules
 * `nextAttemptAt`; 409 means another path already settled the invoice.
 *
 * Uses `onSettled`, not `onSuccess`, for that reason.
 */
export const useRetryInvoiceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (invoiceId: string) => BillingApi.retryInvoiceCharge(invoiceId, uuidv4()),
    {
      onSettled: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

/**
 * `POST /billing/ai-credits/top-up` — buys credit packs (immediate charge,
 * granted on settlement).
 */
export const useTopUpAiCredits = () => {
  const queryClient = useQueryClient();

  return useMutation<TopUpAiCreditsResponse, unknown, { packs: number }>(
    (payload) => BillingApi.topUpAiCredits(payload, uuidv4()),
    {
      onSuccess: () => {
        invalidateBillingCaches(queryClient);
      },
    },
  );
};

export { useSetupSubscriptionMutation } from 'app/modules/settings/modules/billing/hooks/useSetupSubscriptionMutation';
export { useEntitlements } from 'app/modules/settings/modules/billing/hooks/useEntitlements';
export {
  BILLING_CACHE_PREFIX,
  CANCELLATION_STATUS_KEY,
  invalidateBillingCaches,
} from 'app/modules/settings/modules/billing/utils/billingCaches';
